import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import api, {
  AUTH_LOGOUT_EVENT,
  AUTH_TOKEN_REFRESHED_EVENT,
  getAccessTokenExpiration,
  logoutSession,
  renewAccessToken,
  storeAccessToken,
} from "../api";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to normalize user data
  const normalizeUserData = (userData) => {
    if (!userData) return null;
    return {
      ...userData,
      id: userData.id || userData._id, // Ensure id is always present
    };
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          const normalizedUser = normalizeUserData(response.data);
          console.log("Fetched and normalized user:", normalizedUser); // Debug log
          setUser(normalizedUser);
        } catch (error) {
          console.error("Error fetching user:", error);
          // Token renewal and expired-session cleanup are handled by the shared API client.
        }
      }
      setLoading(false);
    };

    fetchUser();
  }, []);

  const updateUser = (userInfo) => {
    const normalizedUser = normalizeUserData(userInfo);
    console.log("Updating user with normalized data:", normalizedUser); // Debug log
    setUser(normalizedUser);
  };

  const clearUser = useCallback(() => {
    setUser(null);
    localStorage.removeItem("token");
  }, []);

  const logout = useCallback(async () => {
    await logoutSession();
    setUser(null);
  }, []);

  useEffect(() => {
    const handleLogout = () => setUser(null);
    window.addEventListener(AUTH_LOGOUT_EVENT, handleLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleLogout);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let renewalTimer;
    const scheduleRenewal = () => {
      window.clearTimeout(renewalTimer);
      const token = localStorage.getItem("token");
      const expiresAt = token ? getAccessTokenExpiration(token) : null;

      if (!expiresAt) {
        clearUser();
        return;
      }

      // Renew one minute before expiry. A failed renewal emits the logout event.
      const delay = Math.max(expiresAt - Date.now() - 60_000, 0);
      renewalTimer = window.setTimeout(() => {
        renewAccessToken().catch(() => {});
      }, delay);
    };

    scheduleRenewal();
    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, scheduleRenewal);
    return () => {
      window.clearTimeout(renewalTimer);
      window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, scheduleRenewal);
    };
  }, [user, clearUser]);

  // Initialize token
  const initializeAuth = (token, userInfo) => {
    storeAccessToken(token);
    updateUser(userInfo);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        updateUser,
        clearUser,
        logout,
        loading,
        api,
        initializeAuth, // Add this new function to the context
      }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
